/*
 * ॐ नमः शिवाय
 * ಓಂ ನಮಃ ಶಿವಾಯ
 * ఓం నమః శివాయ
 * ஓம் நம சிவாய
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 *
 * You are free to use, modify, and distribute this software under the terms 
 * of the VIJAYA 1.0 License.
 *
 * MANDATORY ATTRIBUTION:
 * 1. The attribution to Rudra Coresoft Technologies Private Limited in this 
 *    copyright notice must not be removed even after modifications.
 * 2. For Free License usage, the footer phrases "Powered by arcee.io" and 
 *    "Developed by Rudra Coresoft" (plus logos) must remain visible.
 *
 * See the LICENSE file for the full text.
 */

/**
 * FeedSubPages
 * ------------
 * The athlete sub-pages inside the feed layout: Profile (/feed/profile), the
 * UpVibe-style AthleteProfilePage, and Notification preferences
 * (/feed/notifications), the Dashboard page rendered unchanged, and Analytics
 * (/feed/analytics), the UpVibe app's Analytics tab.
 *
 * Events (/feed/events) and Passes (/feed/passes) join them on phones and
 * tablets only. They render the portal's own pages — the same components the
 * /p/event and /app/my-registrations routes use — inside the feed's middle
 * column, so the bottom tabs stay put and the page no longer arrives with a
 * second header. On a desktop width the nav never links here, and anyone who
 * arrives anyway (a bookmark, a resized window) is sent to the portal path.
 */

import React from 'react';
import { Navigate } from 'react-router-dom';
// Athletes now get the UpVibe-style profile. To restore the old account form
// here, uncomment this import and the line in FeedProfilePage below.
// import UserProfilePage from '../UserProfilePage';
import AthleteProfilePage from './profile/AthleteProfilePage';
import NotificationPreferencesPage from '../NotificationPreferencesPage';
import AnalyticsPage from './analytics/AnalyticsPage';
import PublicTableViewPage from '../PublicTableViewPage';
import PublicPageView from '../PublicPageView';
import { PublicAppProvider } from '../../context/PublicAppContext';
import useShellNav from './useShellNav';
import {
  EVENTS_PATH, EVENTS_SLUG, REGISTRATIONS_PATH, REGISTRATIONS_SLUG,
} from './feedRoutes';

function SubPage({ title, children }) {
  return (
    <main className="fd-sub">
      {title && <div className="fd-sub-h"><h1>{title}</h1></div>}
      {children}
    </main>
  );
}

export function FeedProfilePage() {
  // return <SubPage title="Profile"><UserProfilePage /></SubPage>;
  return <SubPage title="Profile"><AthleteProfilePage /></SubPage>;
}

export function FeedNotificationsPage() {
  // Preferences tab hidden for athletes for now — pass showPreferences (or drop
  // the prop) to bring it back. The Dashboard's copy of this page still shows it.
  // The page carries its own "Notifications" heading, so no feed title here.
  return <SubPage><NotificationPreferencesPage showPreferences={false} /></SubPage>;
}

export function FeedAnalyticsPage() {
  return <SubPage title="Analytics"><AnalyticsPage /></SubPage>;
}

/**
 * Renders the embedded page only while the window is narrow enough to want
 * it; otherwise it hands the visitor to the portal page, which is where the
 * desktop nav points. Resizing a desktop browser past the breakpoint while
 * one of these routes is open therefore lands on the portal page rather than
 * a feed column stretched across a wide screen.
 */
function PhoneAndTabletOnly({ to, children }) {
  const embedded = useShellNav();
  if (!embedded) return <Navigate to={to} replace />;
  return children;
}

/*
 * Both pages read app branding from PublicAppContext, which the /p and /app
 * routes get from PublicAppProvider in App.js. The feed tree has no provider
 * of its own, so each page brings one. Nesting is harmless: these are the
 * only consumers below this point.
 */

export function FeedEventsPage() {
  return (
    <PhoneAndTabletOnly to={EVENTS_PATH}>
      <SubPage>
        <PublicAppProvider>
          <PublicTableViewPage slug={EVENTS_SLUG} />
        </PublicAppProvider>
      </SubPage>
    </PhoneAndTabletOnly>
  );
}

export function FeedPassesPage() {
  return (
    <PhoneAndTabletOnly to={REGISTRATIONS_PATH}>
      <SubPage>
        <PublicAppProvider>
          <PublicPageView slug={REGISTRATIONS_SLUG} embedded />
        </PublicAppProvider>
      </SubPage>
    </PhoneAndTabletOnly>
  );
}
